/**
 * TimelineService - Handles timeline and chronological event viewing
 */

import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  startAfter as firestoreStartAfter,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { deriveSeverityFromLevel } from '../utils/severityCalculator';
import { withTimeout } from '../../../utils/withTimeout';
import { FOLLOW_UP_STATUSES } from '../../../constants/discipline/timelineConfig';
import { getAcademicYearBounds, subscribeAcademicYearBounds } from '../../academicYearService';
import { isCurrentStudent } from '../../../utils/studentEligibility';
import { isVisibleQueueHistoryEvent } from '../../../utils/discipline/queueHistoryVisibility';
import { normalizeTimelineWorkingView, matchesTimelineWorkingView } from '../utils/timelineWorkingView';
import { applyTimelineFilters } from '../utils/timelineFilters';

export function resolveTimelineTimestamp(...candidates) {
  const value = candidates.find(candidate => candidate != null);
  if (!value) return new Date(0);
  const resolved = typeof value === 'string'
    ? new Date(value)
    : value?.toDate
      ? value.toDate()
      : new Date(value);
  return resolved instanceof Date && !Number.isNaN(resolved.getTime())
    ? resolved
    : new Date(0);
}

function timelineCreatedAt(event) {
  return resolveTimelineTimestamp(
    event?.originalData?.createdAt,
    event?.createdAt,
  ).getTime();
}

function timelineTriggerIds(event) {
  if (Array.isArray(event?.triggeredBy)) return event.triggeredBy;
  if (Array.isArray(event?.originalData?.triggeredBy)) {
    return event.originalData.triggeredBy;
  }
  return [];
}

/**
 * Queue and learner history are newest-first. Most records have distinct issue
 * times; older generated consequences can share their source Mention's issue
 * time. In that case the record creation time preserves the real sequence. The
 * causal link is the final deterministic fallback for legacy exact ties.
 */
export function compareTimelineEventsNewestFirst(a, b) {
  const issuedDifference = resolveTimelineTimestamp(b?.timestamp).getTime() -
    resolveTimelineTimestamp(a?.timestamp).getTime();
  if (issuedDifference !== 0) return issuedDifference;

  const createdDifference = timelineCreatedAt(b) - timelineCreatedAt(a);
  if (createdDifference !== 0) return createdDifference;

  if (a?.id && b?.id) {
    if (timelineTriggerIds(a).includes(b.id)) return -1;
    if (timelineTriggerIds(b).includes(a.id)) return 1;
  }

  return 0;
}

export function isOperationalTimelineEvent(event) {
  const record = event?.originalData || event || {};
  return record.test !== true &&
    !String(record.studentId || '').startsWith('sample-class-');
}

export function isVoidedTimelineRecord(record = {}) {
  const source = record.originalData || record;
  const status = String(source.status || record.status || '').toLowerCase();
  const adjudicationStatus = String(
    source.adjudicationStatus || record.adjudicationStatus || ''
  ).toLowerCase();
  return record.voided === true ||
    record.deleted === true ||
    record.isDeleted === true ||
    source.voided === true ||
    source.deleted === true ||
    source.isDeleted === true ||
    status === 'voided' ||
    status === 'deleted' ||
    status === 'cancelled' ||
    adjudicationStatus === 'voided';
}

export const DEFAULT_TIMELINE_PAGE_SIZE = 30;

export function resolveTimelineQueryLimit(limit) {
  const parsed = Number(limit);
  return Number.isFinite(parsed) && parsed > 0
    ? Math.min(Math.floor(parsed), 100)
    : DEFAULT_TIMELINE_PAGE_SIZE;
}

export const TIMELINE_QUERY_TIMEOUT_MS = 10000;

// History is paginated; operational work is not. These are the persisted queue
// states that can still require a person to act. Querying them directly keeps
// reads proportional to the active backlog instead of the school's full history.
export const ACTIVE_WORK_STATUSES = ['pending', 'scheduled', ...FOLLOW_UP_STATUSES];
export const OPEN_FOLLOW_UP_STATUSES = [
  'open',
  'escalation-required',
  'communication-required',
  'communication-failed',
];

export function normalizeAttendanceDerivedStatus(data = {}) {
  const status = data.status || null;
  if (status && !['pending', 'scheduled'].includes(status)) return status;

  switch (data.attendanceStatus) {
    case 'attended':
      return 'served';
    case 'no-show':
    case 'not-attended':
    case 'partial':
      return 'missed';
    case 'absent':
      return 'absent';
    case 'late':
      return 'late';
    case 'excused':
      return 'excused';
    default:
      return status;
  }
}

export function isActiveWorkEvent(event) {
  const record = event?.originalData || event || {};
  if (event?.voided || record.voided || event?.adjudicationStatus === 'voided') return false;
  if (event?.adjudicationStatus === 'pending' || record.adjudicationStatus === 'pending') return true;
  if (!['disciplineQueue', 'queue'].includes(event?.sourceCollection)) return false;

  const status = record.status || event.status;
  const followUpStatus = event.followUpStatus || record.followUpStatus;

  // A completed follow-up closes only the separate communication/escalation
  // task. It must not close a detention that is still waiting to be sat.
  if (['pending', 'scheduled'].includes(status)) return true;
  if (status === 'late' || followUpStatus === 'completed') return false;
  return ACTIVE_WORK_STATUSES.includes(status) ||
    FOLLOW_UP_STATUSES.includes(event.status) ||
    OPEN_FOLLOW_UP_STATUSES.includes(followUpStatus);
}

function timelineQueryTime(entry) {
  const value = entry.doc.data().createdAt;
  return resolveTimelineTimestamp(value).getTime();
}

export function selectMergedTimelinePage(entries, pageSize, isIncluded = () => true) {
  const ordered = [...entries].sort((a, b) => timelineQueryTime(b) - timelineQueryTime(a));
  const selected = [];
  const consumed = [];

  for (const entry of ordered) {
    if (selected.length >= pageSize) break;
    consumed.push(entry);
    if (isIncluded(entry.event)) selected.push(entry);
  }

  return { selected, consumed };
}

export function hasMoreMergedTimelineEntries({
  selected,
  consumed,
  entries,
  pageSize,
  mentionsExhausted,
  consequencesExhausted,
  isIncluded = () => true,
}) {
  if (selected.length < pageSize) return false;
  if (!mentionsExhausted || !consequencesExhausted) return true;

  const consumedEntries = new Set(consumed);
  return entries.some(entry => !consumedEntries.has(entry) && isIncluded(entry.event));
}

export class TimelineService {
  constructor() {
    this.mentionsCollection = 'mentions';
    this.queueCollection = 'disciplineQueue';
    this._activeRelationshipCache = new Map();
  }

  /**
   * Read the current Year for each history request; do not retain an old
   * boundary across rollover, configuration edits or a lost permission.
   * @param {string} schoolId
   * @returns {Promise<Date>}
   */
  async getAcademicYearStart(schoolId) {
    const { start } = await getAcademicYearBounds(schoolId);
    return start;
  }

  /**
   * Fetch every currently active work item without reading completed history.
   * Four single-field queries avoid composite-index coupling and cover:
   * active consequence statuses, explicit follow-up states, and pending review
   * sources in both Queue and Mentions.
   */
  async getActiveWorkEvents(schoolId) {
    const queueRef = collection(db, 'schools', schoolId, this.queueCollection);
    const mentionsRef = collection(db, 'schools', schoolId, this.mentionsCollection);
    const reads = [
      query(queueRef, where('status', 'in', ACTIVE_WORK_STATUSES)),
      query(queueRef, where('followUpStatus', 'in', OPEN_FOLLOW_UP_STATUSES)),
      query(queueRef, where('adjudicationStatus', '==', 'pending')),
      query(mentionsRef, where('adjudicationStatus', '==', 'pending')),
    ].map((activeQuery, index) => withTimeout(
      getDocs(activeQuery),
      TIMELINE_QUERY_TIMEOUT_MS,
      `active Queue query ${index + 1}`,
    ));

    const [statusQueue, followUpQueue, reviewQueue, reviewMentions] = await Promise.all(reads);
    const eventsBySourceAndId = new Map();
    const addQueueDocs = snapshot => snapshot.docs.forEach(queueDoc => {
      const event = this.convertConsequenceToTimelineEvent(queueDoc);
      eventsBySourceAndId.set(`queue:${event.id}`, event);
    });
    addQueueDocs(statusQueue);
    addQueueDocs(followUpQueue);
    addQueueDocs(reviewQueue);
    reviewMentions.docs.forEach(mentionDoc => {
      const event = this.convertMentionToTimelineEvent(mentionDoc);
      eventsBySourceAndId.set(`mention:${event.id}`, event);
    });

    const activeEvents = Array.from(eventsBySourceAndId.values())
      .filter(isOperationalTimelineEvent)
      .filter(isActiveWorkEvent);
    const currentStudentEvents = await this.filterCurrentStudentEvents(schoolId, activeEvents);
    return this.hydrateActiveWorkRelationships(schoolId, currentStudentEvents);
  }

  async filterCurrentStudentEvents(schoolId, events) {
    const studentIds = [...new Set(events.map(event => event.studentId).filter(Boolean))];
    if (studentIds.length === 0) return [];

    const eligibilityEntries = await Promise.all(studentIds.map(async studentId => {
      const studentRef = doc(db, 'schools', schoolId, 'students', studentId);
      const studentSnapshot = await withTimeout(
        getDoc(studentRef),
        TIMELINE_QUERY_TIMEOUT_MS,
        `active learner ${studentId}`,
      );
      return [studentId, studentSnapshot.exists() && isCurrentStudent(studentSnapshot.data())];
    }));
    const eligibilityByStudentId = new Map(eligibilityEntries);

    return events.filter(event => eligibilityByStudentId.get(event.studentId) === true);
  }

  async fetchActiveRelationshipEvent(schoolId, relationshipId) {
    const cacheKey = `${schoolId}:${relationshipId}`;
    if (this._activeRelationshipCache.has(cacheKey)) {
      return this._activeRelationshipCache.get(cacheKey);
    }

    try {
      const mentionRef = doc(db, 'schools', schoolId, this.mentionsCollection, relationshipId);
      const mentionDoc = await withTimeout(
        getDoc(mentionRef),
        TIMELINE_QUERY_TIMEOUT_MS,
        `active related mention ${relationshipId}`,
      );
      if (mentionDoc.exists()) {
        const event = this.convertMentionToTimelineEvent(mentionDoc);
        this._activeRelationshipCache.set(cacheKey, event);
        return event;
      }

      const consequenceRef = doc(db, 'schools', schoolId, this.queueCollection, relationshipId);
      const consequenceDoc = await withTimeout(
        getDoc(consequenceRef),
        TIMELINE_QUERY_TIMEOUT_MS,
        `active related consequence ${relationshipId}`,
      );
      if (consequenceDoc.exists()) {
        const event = this.convertConsequenceToTimelineEvent(consequenceDoc);
        this._activeRelationshipCache.set(cacheKey, event);
        return event;
      }
    } catch (error) {
      console.warn(`[TimelineService] Could not hydrate active relationship ${relationshipId}`, error);
    }
    return null;
  }

  async hydrateActiveWorkRelationships(schoolId, events) {
    const activeIds = new Set(events.map(event => event.id));
    const relationshipById = new Map(events.map(event => [event.id, event]));
    let pendingIds = [...new Set(events.flatMap(event => [
      ...(event.triggeredBy || []),
      ...(event.triggered || []),
    ]))];
    const visitedIds = new Set();

    while (pendingIds.length > 0) {
      const relationshipIds = pendingIds.filter(relationshipId =>
        relationshipId &&
        !visitedIds.has(relationshipId) &&
        !relationshipById.has(relationshipId)
      );
      relationshipIds.forEach(relationshipId => visitedIds.add(relationshipId));
      if (relationshipIds.length === 0) break;

      const relationshipEvents = (await Promise.all(
        relationshipIds.map(relationshipId =>
          this.fetchActiveRelationshipEvent(schoolId, relationshipId)
        )
      )).filter(Boolean);
      pendingIds = [];
      relationshipEvents.forEach(event => {
        relationshipById.set(event.id, event);
        pendingIds.push(...(event.triggeredBy || []), ...(event.triggered || []));
      });
    }

    if (relationshipById.size === events.length) return events;
    return this.buildCausalRelationships(Array.from(relationshipById.values()))
      .filter(event => activeIds.has(event.id));
  }

  /**
   * Keep the complete active-work set live without subscribing to completed
   * history. Each listener uses the same indexed query as the initial read.
   * Emission waits for all four first snapshots so counts and lists never show
   * a temporarily incomplete subset.
   */
  subscribeActiveWorkEvents(schoolId, callback, onError = console.error) {
    const queueRef = collection(db, 'schools', schoolId, this.queueCollection);
    const mentionsRef = collection(db, 'schools', schoolId, this.mentionsCollection);
    const activeQueries = [
      query(queueRef, where('status', 'in', ACTIVE_WORK_STATUSES)),
      query(queueRef, where('followUpStatus', 'in', OPEN_FOLLOW_UP_STATUSES)),
      query(queueRef, where('adjudicationStatus', '==', 'pending')),
      query(mentionsRef, where('adjudicationStatus', '==', 'pending')),
    ];
    const snapshots = new Array(activeQueries.length).fill(null);
    let emissionVersion = 0;
    let active = true;

    const emit = async () => {
      if (snapshots.some(snapshot => snapshot === null)) return;
      const version = ++emissionVersion;
      const eventsBySourceAndId = new Map();
      snapshots.slice(0, 3).forEach(snapshot => {
        snapshot.docs.forEach(queueDoc => {
          const event = this.convertConsequenceToTimelineEvent(queueDoc);
          eventsBySourceAndId.set(`queue:${event.id}`, event);
        });
      });
      snapshots[3].docs.forEach(mentionDoc => {
        const event = this.convertMentionToTimelineEvent(mentionDoc);
        eventsBySourceAndId.set(`mention:${event.id}`, event);
      });
      const activeEvents = Array.from(eventsBySourceAndId.values())
        .filter(isOperationalTimelineEvent)
        .filter(isActiveWorkEvent);
      try {
        const currentStudentEvents = await this.filterCurrentStudentEvents(schoolId, activeEvents);
        const hydratedEvents = await this.hydrateActiveWorkRelationships(schoolId, currentStudentEvents);
        if (active && version === emissionVersion) callback(hydratedEvents);
      } catch (error) {
        if (active && version === emissionVersion) onError(error, activeQueries.length);
      }
    };

    const unsubscribes = activeQueries.map((activeQuery, index) =>
      onSnapshot(activeQuery, snapshot => {
        snapshots[index] = snapshot;
        void emit();
      }, error => onError(error, index))
    );
    return () => {
      active = false;
      emissionVersion += 1;
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }

  /** Complete current history for totals; display pages remain independent. */
  subscribeHistoryRecords(schoolId, view, receive, onError) {
    let active = true;
    let failed = false;
    let unsubscribes = [];
    let generation = 0;
    let snapshots = [null, null];
    const reset = () => {
      generation += 1;
      unsubscribes.forEach(unsubscribe => unsubscribe());
      unsubscribes = []; snapshots = [null, null]; failed = false;
    };
    const workingView = normalizeTimelineWorkingView(view);
    if (workingView.classIds?.length === 0) {
      receive([], { fromCache: false });
      return () => { active = false; };
    }
    const fail = error => {
      if (!active) return;
      reset();
      failed = true;
      onError(error);
    };
    const pending = () => {
      if (!active) return;
      reset(); receive([], { loading: true, fromCache: true });
    };
    const stopYear = subscribeAcademicYearBounds(schoolId, ({ start: academicYearStart }) => {
      if (!active) return;
      pending();
      const currentGeneration = generation;
      const phaseIds = workingView.phaseIds;
      const constraints = phaseIds.length > 0 && phaseIds.length <= 30
        ? [where('phaseContext.phaseId', phaseIds.length === 1 ? '==' : 'in',
          phaseIds.length === 1 ? phaseIds[0] : phaseIds)] : [];
      if (academicYearStart instanceof Date && !Number.isNaN(academicYearStart.getTime())) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(academicYearStart)));
      }
      // Match the paged source's timestamp-field membership as well as its cutoff.
      constraints.push(orderBy('createdAt', 'desc'));
      [this.mentionsCollection, this.queueCollection].forEach((name, index) => {
        const convert = index === 0 ? this.convertMentionToTimelineEvent : this.convertConsequenceToTimelineEvent;
        const unsubscribe = onSnapshot(query(collection(db, 'schools', schoolId, name), ...constraints),
          { includeMetadataChanges: true }, snapshot => {
            if (!active || failed || currentGeneration !== generation) return;
            snapshots[index] = { fromCache: snapshot.metadata?.fromCache === true,
              events: snapshot.docs.map(entry => convert.call(this, entry)) };
            if (snapshots.some(value => value == null)) return;
            const records = snapshots.flatMap(value => value.events).filter(event =>
              isOperationalTimelineEvent(event) && isVisibleQueueHistoryEvent(event)
              && matchesTimelineWorkingView(event, workingView, schoolId)
              && (!(academicYearStart instanceof Date) || Number.isNaN(academicYearStart.getTime())
                || event.timestamp >= academicYearStart));
            receive(records, { fromCache: snapshots.some(value => value.fromCache) });
          }, error => { if (currentGeneration === generation) fail(error); });
        unsubscribes.push(unsubscribe);
      });
    }, fail, pending);
    return () => { active = false; stopYear(); reset(); };
  }

  /**
   * Get all timeline events (mentions + consequences)
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} Timeline events
   */
  async getTimelineEvents(filters = {}) {
    try {
      // Guard: require schoolId to prevent permission errors
      if (!filters.schoolId) {
        console.warn('TimelineService: schoolId required but not provided');
        return { events: [], hasMore: false, oldestTimestamp: null };
      }

      // Queue opens on a bounded server page. The previous implementation read
      // both complete collections and only then filtered/sliced in the browser,
      // which made the initial Queue load grow with the school's entire history.
      // Each source keeps its own cursor because Mentions and consequences are
      // separate Firestore collections that are merged into one timeline.
      const queryLimit = resolveTimelineQueryLimit(filters.limit);

      const workingView = normalizeTimelineWorkingView(filters);
      const { phaseIds } = workingView;
      const pageCursor = filters.pageCursor || {};
      if (workingView.classIds?.length === 0) {
        return { events: [], hasMore: false, oldestTimestamp: null, openActionable: [], pageCursor: null };
      }
      const academicYearStart = await this.getAcademicYearStart(filters.schoolId);
      const scopeKey = JSON.stringify([filters.schoolId, workingView,
        filters.searchQuery || null, filters.status || null, filters.startDate || null, filters.endDate || null, filters.severity || null,
        academicYearStart?.getTime?.() ?? null]);
      if ((pageCursor.scopeKey && pageCursor.scopeKey !== scopeKey)
        || ((phaseIds.length || workingView.classIds != null || filters.searchQuery || filters.status)
          && !pageCursor.scopeKey && (pageCursor.mentions || pageCursor.consequences))) {
        throw new Error('The Queue phase selection changed, its working scope changed, or the school Year changed. Reload its first page.');
      }
      const matchesView = event => matchesTimelineWorkingView(event, workingView, filters.schoolId);
      // Push ordinary phase selections into the indexed read. A school can
      // define more phases than a Firestore disjunction permits; those broader
      // selections retain the same complete-result predicate through refill.
      const phaseConstraints = phaseIds.length > 0 && phaseIds.length <= 30
        ? [where('phaseContext.phaseId', phaseIds.length === 1 ? '==' : 'in',
          phaseIds.length === 1 ? phaseIds[0] : phaseIds)] : [];
      const useServerPage = !filters.eventType && !filters.studentId;

      const pageConstraints = (cursor) => {
        const constraints = [...phaseConstraints];
        if (academicYearStart instanceof Date && !Number.isNaN(academicYearStart.getTime())) {
          constraints.push(where('createdAt', '>=', Timestamp.fromDate(academicYearStart)));
        }
        constraints.push(orderBy('createdAt', 'desc'));
        if (cursor) constraints.push(firestoreStartAfter(cursor));
        constraints.push(firestoreLimit(queryLimit));
        return constraints;
      };

      let mentionsSnapshot = null;
      let consequencesSnapshot = null;

      // Fetch mentions — always query (eventType might be a mention subtype like demerit, merit, positive)
      {
        const mentionsConstraints = [...phaseConstraints];

        if (filters.studentId) {
          mentionsConstraints.push(where('studentId', '==', filters.studentId));
        }

        // Filter by mentionType when a specific event type is requested (not 'mention' = all mentions)
        if (filters.eventType && filters.eventType !== 'mention') {
          mentionsConstraints.push(where('mentionType', '==', filters.eventType));
        }

        // HIERARCHICAL ONLY: Migration completed, flat collection no longer used
        // Don't use orderBy - fetch all and sort client-side to include mentions without issuedAt

        console.log(`[TimelineService] 📖 READING mentions for schoolId: ${filters.schoolId}`);

        // Fetch from hierarchical path only
        const hierarchicalQuery = query(
          collection(db, 'schools', filters.schoolId, 'mentions'),
          ...(useServerPage ? pageConstraints(pageCursor.mentions) : mentionsConstraints)
        );
        mentionsSnapshot = withTimeout(
          getDocs(hierarchicalQuery),
          TIMELINE_QUERY_TIMEOUT_MS,
          'mentions query'
        );
      }

      // Fetch consequences (all types from disciplineQueue)
      // Only skip if explicitly filtering for mentions only
      if (!filters.eventType || filters.eventType !== 'mention') {
        // Build query constraints array
        const consequenceConstraints = [...phaseConstraints];

        if (filters.studentId) {
          consequenceConstraints.push(where('studentId', '==', filters.studentId));
        }

        // Only add type filter if a specific non-mention type is requested
        if (filters.eventType && filters.eventType !== 'mention') {
          consequenceConstraints.push(where('type', '==', filters.eventType));
        }

        // HIERARCHICAL ONLY: Migration completed, flat collection no longer used
        // Don't use orderBy - fetch all and sort client-side to include consequences without issuedAt

        console.log(`[TimelineService] 📖 READING disciplineQueue for schoolId: ${filters.schoolId}`);

        // Fetch from hierarchical path only
        const hierarchicalQuery = query(
          collection(db, 'schools', filters.schoolId, 'disciplineQueue'),
          ...(useServerPage ? pageConstraints(pageCursor.consequences) : consequenceConstraints)
        );
        consequencesSnapshot = withTimeout(
          getDocs(hierarchicalQuery),
          TIMELINE_QUERY_TIMEOUT_MS,
          'consequences query'
        );
      }

      // These independent reads must not block one another. On production data,
      // running them sequentially added the full Mentions latency to the full
      // consequence latency before Queue could render anything.
      const [resolvedMentions, resolvedConsequences] = await Promise.all([
        mentionsSnapshot || Promise.resolve(null),
        consequencesSnapshot || Promise.resolve(null),
      ]);

      const pageEntries = [];
      const appendMentionDocs = docs => {
        pageEntries.push(...docs.map(doc => ({
          doc,
          source: 'mentions',
          event: this.convertMentionToTimelineEvent(doc),
        })));
      };
      const appendConsequenceDocs = docs => {
        pageEntries.push(...docs.map(doc => ({
          doc,
          source: 'consequences',
          event: this.convertConsequenceToTimelineEvent(doc),
        })));
      };
      if (resolvedMentions) {
        console.log(`[TimelineService] 📊 Mentions page: ${resolvedMentions.size} docs`);
        appendMentionDocs(resolvedMentions.docs);
      }
      if (resolvedConsequences) {
        console.log(`[TimelineService] 📊 Consequences page: ${resolvedConsequences.size} docs`);
        appendConsequenceDocs(resolvedConsequences.docs);
      }

      // Exclude items from previous academic years (stale records, graduated
      // students). Mirrors the teacher-dashboard cutoff and reads the same
      // source (schools/{id}/schoolDatePeriods, type === 'year'). Items whose
      // timestamp can't be resolved are kept (treated as current) so we never
      // hide a live item; only events provably dated before the year start are
      // dropped. This runs BEFORE the 50-event window so the window is filled
      // with current-year items, not pushed out by stale ones.
      const isWithinTimelineBoundary = (event) => {
        if (!isOperationalTimelineEvent(event) || !matchesView(event)) return false;
        if (academicYearStart instanceof Date && !isNaN(academicYearStart)) {
          const cutoff = academicYearStart.getTime();
          const t = event.timestamp?.getTime?.() ?? new Date(event.timestamp).getTime();
          if (!isNaN(t) && t < cutoff) return false;
        }
        return true;
      };
      const isIncluded = event =>
        isWithinTimelineBoundary(event) && isVisibleQueueHistoryEvent(event)
        && applyTimelineFilters([event], filters).length > 0;

      // The Queue is one timeline, not two equal lists. Read ahead from both
      // collections, merge by the shared Firestore ordering field, and consume
      // only the sources that contribute to this 30-event page. This prevents
      // consequence-heavy periods from producing an artificial orphan tail.
      let { selected, consumed } = useServerPage
        ? selectMergedTimelinePage(pageEntries, queryLimit, isIncluded)
        : {
            selected: pageEntries.filter(entry => isIncluded(entry.event)),
            consumed: pageEntries,
          };
      let mentionsExhausted = !resolvedMentions || resolvedMentions.size < queryLimit;
      let consequencesExhausted = !resolvedConsequences || resolvedConsequences.size < queryLimit;
      let mentionReadCursor = resolvedMentions?.docs.at(-1) || pageCursor.mentions || null;
      let consequenceReadCursor = resolvedConsequences?.docs.at(-1) || pageCursor.consequences || null;

      // A Firestore limit applies before client-only integrity exclusions such
      // as test/sample/audit records. A full 30-card result is not sufficient:
      // hidden rows can also keep one source's read frontier newer than the
      // oldest selected card, producing a full but chronologically false page
      // (for example 27 Mentions and only three linked consequences). Keep
      // scanning a source until its frontier passes the selected cutoff.
      if (useServerPage) {
        const shouldReadSource = (exhausted, readCursor) => {
          if (exhausted || !readCursor) return false;
          if (selected.length < queryLimit) return true;

          const selectedCutoff = timelineQueryTime(selected.at(-1));
          const sourceFrontier = resolveTimelineTimestamp(
            readCursor.data().createdAt
          ).getTime();
          return sourceFrontier >= selectedCutoff;
        };

        for (;;) {
          const readMentions = shouldReadSource(mentionsExhausted, mentionReadCursor);
          const readConsequences = shouldReadSource(
            consequencesExhausted,
            consequenceReadCursor
          );
          if (!readMentions && !readConsequences) break;

          const [nextMentions, nextConsequences] = await Promise.all([
            readMentions
              ? withTimeout(
                  getDocs(query(
                    collection(db, 'schools', filters.schoolId, 'mentions'),
                    ...pageConstraints(mentionReadCursor),
                  )),
                  TIMELINE_QUERY_TIMEOUT_MS,
                  'mentions page refill',
                )
              : Promise.resolve(null),
            readConsequences
              ? withTimeout(
                  getDocs(query(
                    collection(db, 'schools', filters.schoolId, 'disciplineQueue'),
                    ...pageConstraints(consequenceReadCursor),
                  )),
                  TIMELINE_QUERY_TIMEOUT_MS,
                  'consequences page refill',
                )
              : Promise.resolve(null),
          ]);

          if (nextMentions) {
            appendMentionDocs(nextMentions.docs);
            mentionReadCursor = nextMentions.docs.at(-1) || mentionReadCursor;
            mentionsExhausted = nextMentions.size < queryLimit;
          }
          if (nextConsequences) {
            appendConsequenceDocs(nextConsequences.docs);
            consequenceReadCursor = nextConsequences.docs.at(-1) || consequenceReadCursor;
            consequencesExhausted = nextConsequences.size < queryLimit;
          }

          ({ selected, consumed } = selectMergedTimelinePage(pageEntries, queryLimit, isIncluded));
        }
      }

      const selectedIds = new Set(selected.map(entry => entry.event.id));
      const relationshipById = new Map(selected.map(entry => [entry.event.id, entry]));
      const availableById = new Map(pageEntries.map(entry => [entry.event.id, entry]));
      let pendingRelationshipIds = selected.flatMap(entry => [
        ...(entry.event.triggeredBy || []),
        ...(entry.event.triggered || []),
      ]);
      const visitedRelationshipIds = new Set();

      // Keep causal cards intact at a page boundary. Most trigger records are
      // already in the two read-ahead windows, so this normally adds no reads.
      // If a referenced trigger sits beyond the window, fetch that exact record.
      while (pendingRelationshipIds.length > 0) {
        const relationshipIds = [...new Set(pendingRelationshipIds)].filter(relationshipId =>
          relationshipId &&
          !visitedRelationshipIds.has(relationshipId) &&
          !relationshipById.has(relationshipId)
        );
        pendingRelationshipIds = [];
        relationshipIds.forEach(relationshipId => visitedRelationshipIds.add(relationshipId));

        const relationshipEntries = await Promise.all(relationshipIds.map(async relationshipId => {
          const availableEntry = availableById.get(relationshipId);
          if (availableEntry || !useServerPage) return availableEntry || null;

          try {
            const mentionRef = doc(db, 'schools', filters.schoolId, 'mentions', relationshipId);
            const mentionDoc = await withTimeout(
              getDoc(mentionRef),
              TIMELINE_QUERY_TIMEOUT_MS,
              `related mention ${relationshipId}`,
            );
            if (mentionDoc.exists()) {
              return {
                doc: mentionDoc,
                source: 'mentions',
                event: this.convertMentionToTimelineEvent(mentionDoc),
              };
            }

            const consequenceRef = doc(db, 'schools', filters.schoolId, 'disciplineQueue', relationshipId);
            const consequenceDoc = await withTimeout(
              getDoc(consequenceRef),
              TIMELINE_QUERY_TIMEOUT_MS,
              `related consequence ${relationshipId}`,
            );
            return consequenceDoc.exists() ? {
              doc: consequenceDoc,
              source: 'consequences',
              event: this.convertConsequenceToTimelineEvent(consequenceDoc),
            } : null;
          } catch (error) {
            console.warn(`[TimelineService] Could not hydrate relationship ${relationshipId}`, error);
            return null;
          }
        }));

        relationshipEntries.forEach(relationshipEntry => {
          // A hidden source record may still provide causal context for a
          // visible consequence card. It must not occupy a page slot, but it
          // remains eligible for relationship hydration within the same
          // operational/current-year boundary.
          if (relationshipEntry && isWithinTimelineBoundary(relationshipEntry.event)) {
            relationshipById.set(relationshipEntry.event.id, relationshipEntry);
            pendingRelationshipIds.push(
              ...(relationshipEntry.event.triggeredBy || []),
              ...(relationshipEntry.event.triggered || []),
            );
          }
        });
      }

      const relationshipEvents = Array.from(relationshipById.values(), entry => entry.event);

      const sortedEvents = relationshipEvents.sort(compareTimelineEventsNewestFirst);
      const eventsWithRelationships = this.buildCausalRelationships(sortedEvents)
        .filter(event => selectedIds.has(event.id));

      // Action tabs are operational queues, never a view onto the first history
      // page. Fetch the complete active backlog separately for their counts and
      // working rosters. Do not merge it into Queue All: status-based active work
      // has no history cursor or academic-year bound and would turn the apparent
      // next page into stale consequence spillover without its source Mentions.
      let openActionable = [];
      if (filters.includeOpenActionable) {
        try {
          openActionable = await this.getActiveWorkEvents(filters.schoolId);
          openActionable = openActionable.filter(matchesView);
        } catch (error) {
          // Active work is optional augmentation. A denied, timed-out, or malformed
          // learner lookup must never discard a successfully loaded history page.
          console.warn(
            '[TimelineService] Active Queue work unavailable; returning Queue History without it',
            error,
          );
        }
      }

      // Apply additional filters
      const filteredEvents = this.applyTimelineFilters(eventsWithRelationships, filters);

      const hasMore = Boolean(useServerPage && hasMoreMergedTimelineEntries({
        selected,
        consumed,
        entries: pageEntries,
        pageSize: queryLimit,
        mentionsExhausted,
        consequencesExhausted,
        isIncluded,
      }));

      const consumedCursor = consumed.reduce((cursor, entry) => ({
        ...cursor,
        [entry.source]: entry.doc,
      }), { ...pageCursor });
      const nextPageCursor = useServerPage ? {
        scopeKey,
        mentions: consumedCursor.mentions || null,
        consequences: consumedCursor.consequences || null,
      } : null;

      return {
        events: filteredEvents,
        hasMore,
        oldestTimestamp: filteredEvents.length > 0
          ? filteredEvents[filteredEvents.length - 1].timestamp
          : null,
        openActionable,
        pageCursor: nextPageCursor,
      };
    } catch (error) {
      console.error('Error fetching timeline events:', error);
      throw error;
    }
  }

  /**
   * Convert mention document to timeline event
   * @param {Object} doc - Firestore document
   * @returns {Object} Timeline event
   */
  convertMentionToTimelineEvent(doc) {
    const data = doc.data();

    const timestamp = resolveTimelineTimestamp(
      data.issuedAt,
      data.createdAt,
      data.timestamp,
    );

    // Use the actual mentionType from the document (matches Cloud Function counter)
    // This ensures the badge displays "Demerit 5" instead of "Mention 5"
    const mentionType = data.mentionType || 'mention';

    return {
      id: doc.id,
      type: mentionType,
      eventType: mentionType,
      consequenceType: null,
      studentId: data.studentId,
      studentName: data.studentName,
      classId: data.classId,
      className: data.className,
      offence: data.offence || data.reason,
      timestamp: timestamp,
      status: data.actioned ? 'resolved' : 'active',
      severity: data.severity || deriveSeverityFromLevel(data.level || 1),
      notes: data.details || data.notes || '',
      attachments: Array.isArray(data.attachments) ? data.attachments : [],
      issuedByName: data.issuedByName,
      triggeredBy: [],
      triggered: data.triggeredConsequences || [],
      sourceCollection: 'mentions',
      originalData: data,
      schoolId: data.schoolId,
      sequentialNumber: data.sequentialNumber || null,
      // Additional context
      category: data.offenseCategory || 'uncategorized',
      level: data.level || 1,
      adjudicationStatus: data.adjudicationStatus || null,
      adjudicationId: data.adjudicationId || null
    };
  }

  /**
   * Resolve the consequence type from Firestore data.
   * Shared by convertConsequenceToTimelineEvent and real-time listener MODIFIED handler
   * to ensure consistent type resolution.
   * @param {Object} data - Firestore document data
   * @returns {string} Resolved consequence type
   */
  resolveConsequenceType(data) {
    let type = data.type || data.consequenceType;

    // Handle legacy data where type is numeric or generic and value has real type
    // Legacy consequences stored actual type in 'value' field (e.g., type="1", value="demerit")
    if ((!type || type === 'consequence' || !isNaN(type)) && data.value) {
      type = data.value;
    }

    // If type is 'mention' or if this looks like a mention (has reason but no consequenceType)
    if (type === 'mention' || (!type && data.reason && !data.consequenceType)) {
      type = 'mention';
    }

    return type || 'consequence';
  }

  /**
   * Convert consequence document to timeline event
   * @param {Object} doc - Firestore document
   * @returns {Object} Timeline event
   */
  convertConsequenceToTimelineEvent(doc) {
    const data = doc.data();
    const type = this.resolveConsequenceType(data);

    // Map statuses to consistent terminology
    let status;
    if (type === 'demerit') {
      status = data.undone ? 'resolved' : 'active';
    } else if (type === 'detention' || type?.includes('detention') || type === 'suspension' || type?.includes('suspension')) {
      const normalizedStatus = normalizeAttendanceDerivedStatus(data);
      // Explicit status takes priority — servedDate is only a fallback when status is missing/ambiguous
      if (normalizedStatus === 'served') {
        status = 'served';
      } else if (normalizedStatus === 'missed' || normalizedStatus === 'absent' || normalizedStatus === 'late') {
        status = normalizedStatus;
      } else if (normalizedStatus === 'cancelled' || data.undone) {
        status = 'excused';
      } else if (normalizedStatus === 'excused') {
        status = 'excused';
      } else if (normalizedStatus === 'escalated' || normalizedStatus === 'rescheduled') {
        status = normalizedStatus;
      } else if (data.servedDate && (!normalizedStatus || normalizedStatus === 'pending')) {
        status = 'served';
      } else if (normalizedStatus === 'pending' || normalizedStatus === 'scheduled' || !normalizedStatus) {
        status = 'scheduled';
      } else {
        status = normalizedStatus || 'scheduled';
      }
    } else {
      // All other consequence types — read Firestore status directly
      const normalizedStatus = normalizeAttendanceDerivedStatus(data);
      if (normalizedStatus === 'served') {
        status = 'served';
      } else if (['missed', 'absent', 'late', 'escalated', 'rescheduled', 'scheduled'].includes(normalizedStatus)) {
        status = normalizedStatus;
      } else if (normalizedStatus === 'cancelled' || data.undone) {
        status = 'excused';
      } else if (normalizedStatus === 'excused') {
        status = 'excused';
      } else if (normalizedStatus === 'pending' || !normalizedStatus) {
        status = 'scheduled';
      } else {
        status = normalizedStatus || 'active';
      }
    }

    const timestamp = resolveTimelineTimestamp(
      data.issuedAt,
      data.createdAt,
    );

    return {
      id: doc.id,
      type: type,
      consequenceType: type, // Keep action filtering aligned with the saved final type.
      eventType: type, // Ensure we have eventType for backwards compatibility
      studentId: data.studentId,
      studentName: data.studentName,
      classId: data.classId,
      className: data.className,
      offence: data.offenceName || data.offence || data.reason,
      timestamp: timestamp,
      status: status,
      severity: data.severity || 'medium',
      notes: data.notes || '',
      attachments: Array.isArray(data.attachments) ? data.attachments : [],
      issuedByName: data.issuedByName || data.assignedByName || data.createdByName,
      triggeredBy: data.triggeredBy || data.triggeringMentions || data.triggeringConsequences || data.triggeringDemerits || [],
      triggered: [],
      sourceCollection: 'disciplineQueue',
      originalData: data,
      schoolId: data.schoolId,
      sequentialNumber: data.sequentialNumber || null, // Sequential counter from Cloud Function
      // Additional context
      ruleName: data.ruleName,
      autoGenerated: data.autoGenerated || false,
      scheduledDate: data.scheduledDate?.toDate?.() || data.scheduledDate,
      servedDate: data.servedDate?.toDate?.() || data.servedDate,
      attendanceStatus: data.attendanceStatus || null,
      followUpStatus: data.followUpStatus || null,
      adjudicationStatus: data.adjudicationStatus || null,
      adjudicationId: data.adjudicationId || null,
      value: data.value // Include value field for legacy data
    };
  }

  /**
   * Fetch trigger event details by IDs
   * @param {Array<string>} triggerIds - IDs of trigger events
   * @returns {Promise<Array>} Trigger event details
   */
  async fetchTriggerDetails(triggerIds) {
    if (!triggerIds || triggerIds.length === 0) return [];

    try {
      const triggerDetails = [];

      // Fetch from mentions collection
      for (const triggerId of triggerIds) {
        try {
          const mentionDoc = await getDoc(doc(db, this.mentionsCollection, triggerId));
          if (mentionDoc.exists()) {
            const data = mentionDoc.data();
            triggerDetails.push({
              id: mentionDoc.id,
              type: 'mention',
              offence: data.offence || data.reason,
              level: data.level,
              timestamp: data.issuedAt?.toDate?.() || data.createdAt?.toDate?.() || new Date(data.issuedAt || data.createdAt),
              studentId: data.studentId,
              studentName: data.studentName
            });
            continue;
          }
        } catch (error) {
          console.error(`Error fetching mention ${triggerId}:`, error);
        }

        // If not found in mentions, try disciplineQueue
        try {
          const queueDoc = await getDoc(doc(db, this.queueCollection, triggerId));
          if (queueDoc.exists()) {
            const data = queueDoc.data();
            triggerDetails.push({
              id: queueDoc.id,
              type: this.resolveConsequenceType(data),
              offence: data.offenceName || data.offence || data.reason,
              level: data.level,
              timestamp: data.createdAt?.toDate?.() || new Date(data.createdAt),
              studentId: data.studentId,
              studentName: data.studentName
            });
          }
        } catch (error) {
          console.error(`Error fetching queue item ${triggerId}:`, error);
        }
      }

      return triggerDetails;
    } catch (error) {
      console.error('Error fetching trigger details:', error);
      return [];
    }
  }

  /**
   * Build causal relationships between events
   * @param {Array} events - Timeline events
   * @returns {Array} Events with relationships
   */
  buildCausalRelationships(events) {
    const sorted = events.sort(compareTimelineEventsNewestFirst);

    // Create a map for quick lookups
    const eventMap = new Map(sorted.map(e => [e.id, e]));

    // First pass: bi-directional 'triggered' links so each cause knows what it led to.
    sorted.forEach(event => {
      if (event.triggeredBy && event.triggeredBy.length > 0) {
        event.triggeredBy.forEach(triggererId => {
          const triggerEvent = eventMap.get(triggererId);
          if (triggerEvent) {
            if (!triggerEvent.triggered) triggerEvent.triggered = [];
            if (!triggerEvent.triggered.includes(event.id)) {
              triggerEvent.triggered.push(event.id);
            }
          }
        });
      }
    });

    // Recursively build a detail node for a trigger, carrying its OWN constituents
    // so the full causal chain is self-contained: a detention's demerits, and each
    // demerit's mentions. `seen` guards against cycles. The flat fields are kept as
    // before for backward compatibility; nested `triggeredByDetails` is additive.
    const buildDetail = (triggerEvent, seen) => {
      const node = {
        id: triggerEvent.id,
        type: triggerEvent.type,
        offence: triggerEvent.offence,
        level: triggerEvent.level,
        timestamp: triggerEvent.timestamp,
        studentId: triggerEvent.studentId,
        studentName: triggerEvent.studentName,
        sourceCollection: triggerEvent.sourceCollection,
        subject: triggerEvent.originalData?.subject || triggerEvent.subject || null,
        issuedByName: triggerEvent.issuedByName || null,
      };
      if (triggerEvent.triggeredBy && triggerEvent.triggeredBy.length > 0 && !seen.has(triggerEvent.id)) {
        const nextSeen = new Set(seen).add(triggerEvent.id);
        const children = [];
        triggerEvent.triggeredBy.forEach(childId => {
          const childEvent = eventMap.get(childId);
          if (childEvent) children.push(buildDetail(childEvent, nextSeen));
        });
        if (children.length > 0) node.triggeredByDetails = children;
      }
      return node;
    };

    // Second pass: populate recursive source and result details for display.
    sorted.forEach(event => {
      if (event.triggeredBy && event.triggeredBy.length > 0) {
        const seen = new Set([event.id]);
        const triggeredByDetails = [];
        event.triggeredBy.forEach(triggererId => {
          const triggerEvent = eventMap.get(triggererId);
          if (triggerEvent) triggeredByDetails.push(buildDetail(triggerEvent, seen));
        });
        if (triggeredByDetails.length > 0) {
          const existingDetails = event.originalData.triggeredByDetails || [];
          const completeTriggeredByDetails = [...new Map(
            [...triggeredByDetails, ...existingDetails].map(detail => [detail.id, detail])
          ).values()];
          event.originalData.triggeredByDetails = completeTriggeredByDetails;

          // Consequences are system-created records, but their human and subject
          // context belongs to the Mentions that caused them. Older consequence
          // documents did not copy those fields, so derive them from the fully
          // hydrated causal tree instead of making the UI depend on storage era.
          const sourceSubjects = new Set();
          const sourceIssuedByNames = new Set();
          const collectSourceContext = (detail) => {
            if (detail.subject) sourceSubjects.add(detail.subject);
            if (detail.issuedByName) sourceIssuedByNames.add(detail.issuedByName);
            (detail.triggeredByDetails || []).forEach(collectSourceContext);
          };
          completeTriggeredByDetails.forEach(collectSourceContext);
          event.sourceSubjects = [...sourceSubjects];
          event.sourceIssuedByNames = [...sourceIssuedByNames];
        }
      }
      if (event.triggered && event.triggered.length > 0) {
        const seen = new Set([event.id]);
        const triggeredDetails = [];
        event.triggered.forEach(triggeredId => {
          const triggeredEvent = eventMap.get(triggeredId);
          if (triggeredEvent) triggeredDetails.push(buildDetail(triggeredEvent, seen));
        });
        if (triggeredDetails.length > 0) {
          const existingDetails = event.originalData.triggeredDetails || [];
          event.originalData.triggeredDetails = [...new Map(
            [...triggeredDetails, ...existingDetails].map(detail => [detail.id, detail])
          ).values()];
        }
      }
    });

    return sorted;
  }

  /**
   * Apply filters to timeline events
   * @param {Array} events - Timeline events
   * @param {Object} filters - Filter options
   * @returns {Array} Filtered events
   */
  applyTimelineFilters(events, filters) {
    return applyTimelineFilters(events, filters);
  }

  /**
   * Get timeline statistics
   * @param {Array} events - Timeline events
   * @returns {Object} Statistics
   */
  getTimelineStats(events) {
    const stats = {
      total: events.length,
      mentions: events.filter(e => e.type === 'mention').length,
      demerits: events.filter(e => e.type === 'demerit').length,
      detentions: events.filter(e => e.type === 'detention').length,
      suspensions: events.filter(e => e.type === 'suspension').length,
      // Status breakdown
      active: events.filter(e => e.status === 'active').length,
      resolved: events.filter(e => e.status === 'resolved').length,
      scheduled: events.filter(e => e.status === 'scheduled').length,
      served: events.filter(e => e.status === 'served').length,
      excused: events.filter(e => e.status === 'excused').length,
      // Severity breakdown
      bySeverity: {
        low: events.filter(e => e.severity === 'low').length,
        medium: events.filter(e => e.severity === 'medium').length,
        high: events.filter(e => e.severity === 'high').length,
        critical: events.filter(e => e.severity === 'critical').length
      }
    };

    return stats;
  }

  /**
   * Get trigger context for display
   * @param {Object} event - Timeline event
   * @param {Array} allEvents - All timeline events
   * @returns {string|null} Trigger context message
   */
  getTriggerContext(event, allEvents) {
    if (!event.triggeredBy || event.triggeredBy.length === 0) {
      return null;
    }

    const triggerEvents = allEvents.filter(e => event.triggeredBy.includes(e.id));
    if (event.type === 'demerit') {
      const mentionCount = triggerEvents.filter(e => e.type === 'mention').length;
      if (mentionCount > 0) {
        return `${mentionCount} mention${mentionCount > 1 ? 's' : ''} triggered this demerit`;
      }
    } else if (event.type === 'detention') {
      const demeritCount = triggerEvents.filter(e => e.type === 'demerit').length;
      const mentionCount = triggerEvents.filter(e => e.type === 'mention').length;

      if (demeritCount > 0) {
        return `${demeritCount} demerit${demeritCount > 1 ? 's' : ''} triggered this detention`;
      } else if (mentionCount > 0) {
        return `${mentionCount} mention${mentionCount > 1 ? 's' : ''} triggered this detention`;
      }
    } else if (event.type === 'suspension') {
      const detentionCount = triggerEvents.filter(e => e.type === 'detention').length;
      if (detentionCount > 0) {
        return `${detentionCount} detention${detentionCount > 1 ? 's' : ''} triggered this suspension`;
      }
      return 'Multiple violations triggered this suspension';
    }

    return `Triggered by ${triggerEvents.length} previous event${triggerEvents.length > 1 ? 's' : ''}`;
  }

  /**
   * Build timeline from cached raw data (already fetched, not Firestore docs)
   * Used for deriving timeline view from cached mentions + consequences
   * @param {Array} mentions - Raw mention objects from cache
   * @param {Array} consequences - Raw consequence objects from cache
   * @param {Array} messages - Optional message status data
   * @returns {Array} Timeline events with relationships
   */
  buildTimelineFromRawData(mentions = [], consequences = [], messages = []) {
    const events = [];

    // Build message status map for efficient lookup
    const messageStatusMap = new Map();
    messages.forEach(msg => {
      if (msg.metadata?.mentionId) {
        messageStatusMap.set(msg.metadata.mentionId, {
          status: msg.status,
          response: msg.delivery?.telegram?.response,
          respondedAt: msg.delivery?.telegram?.respondedAt,
          createdAt: msg.createdAt
        });
      }
    });

    // Transform mentions into timeline events
    mentions.forEach(mention => {
      if (isVoidedTimelineRecord(mention)) return;
      // Safe timestamp conversion - use issuedAt as primary field
      let timestamp;
      const tsField = mention.issuedAt || mention.createdAt || mention.timestamp;
      if (tsField) {
        if (typeof tsField === 'string') {
          timestamp = new Date(tsField);
        } else if (tsField.toDate) {
          timestamp = tsField.toDate();
        } else if (tsField instanceof Date) {
          timestamp = tsField;
        } else {
          timestamp = new Date(tsField);
        }
      } else {
        timestamp = new Date();
      }

      // Use the actual mentionType from the document (matches Cloud Function counter)
      const mentionType = mention.mentionType || 'mention';

      const event = {
        id: mention.id,
        type: mentionType,
        eventType: mentionType,
        sourceCollection: 'mentions',
        studentId: mention.studentId,
        studentName: mention.studentName,
        className: mention.className,
        classId: mention.classId,
        offence: mention.offence || mention.reason,
        timestamp: timestamp,
        status: mention.status || 'active',
        createdBy: mention.createdBy || mention.teacherId,
        teacherName: mention.teacherName,
        category: mention.category,
        offenseCategory: mention.offenseCategory,
        level: mention.level,
        severity: deriveSeverityFromLevel(mention.level),
        notes: mention.notes,
        attachments: Array.isArray(mention.attachments) ? mention.attachments : [],
        triggered: mention.triggered,
        sequentialNumber: mention.sequentialNumber || null,
        originalData: mention
      };

      // Add message status if available
      const messageStatus = messageStatusMap.get(mention.id);
      if (messageStatus) {
        event.messageStatus = messageStatus;
      }

      events.push(event);
    });

    // Transform consequences into timeline events
    consequences.forEach(consequence => {
      if (isVoidedTimelineRecord(consequence)) return;
      // Skip adjudication audit breadcrumbs (e.g. adjudication_submitted). They
      // are internal log entries for "sent to / resolved by review" — the real
      // consequence already appears on the timeline with its Under Review state,
      // so rendering the breadcrumb as its own card just duplicates it. The
      // discipline queue filters these the same way (DisciplineQueueRefactored
      // isCleanEvent); the profile must match.
      if (consequence.source === 'adjudication' || consequence.type?.startsWith?.('adjudication_')) {
        return;
      }

      let type = consequence.type || consequence.consequenceType;

      // Handle legacy data
      if ((!type || type === 'consequence' || !isNaN(type)) && consequence.value) {
        type = consequence.value;
      }

      // If type is 'mention' or looks like a mention
      if (type === 'mention' || (!type && consequence.reason && !consequence.consequenceType)) {
        type = 'mention';
      }

      // Safe timestamp conversion
      let timestamp;
      const tsField = consequence.issuedAt || consequence.createdAt || consequence.timestamp;
      if (tsField) {
        if (typeof tsField === 'string') {
          timestamp = new Date(tsField);
        } else if (tsField.toDate) {
          timestamp = tsField.toDate();
        } else if (tsField instanceof Date) {
          timestamp = tsField;
        } else {
          timestamp = new Date(tsField);
        }
      } else {
        timestamp = new Date();
      }

      // The event IS the consequence — spread the whole record so nothing
      // (consequenceDetails, adjudicationStatus, attachments, …) is silently
      // dropped, then layer on only the derived fields the timeline needs.
      const event = {
        ...consequence,
        type: type,
        eventType: type,
        consequenceType: type,
        sourceCollection: 'disciplineQueue',
        offence: consequence.offenceName || consequence.offence || consequence.reason,
        timestamp: timestamp,
        status: consequence.status || 'scheduled',
        severity: deriveSeverityFromLevel(consequence.level),
        triggeredBy: consequence.triggeredBy || [],
        originalData: consequence
      };

      events.push(event);
    });

    events.sort(compareTimelineEventsNewestFirst);

    // Build causal relationships
    return this.buildCausalRelationships(events);
  }
}
